import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Volume2,
  VolumeX,
  Play,
  Pause,
  RotateCcw,
  Download,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Music,
  RefreshCw,
  Search,
  Trash2,
  Filter,
  X,
  Copy,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGenerateAudioMutation,
  useLazyGetPredictionAudioQuery,
  useListPredictionAudiosQuery,
  useDeletePredictionAudioMutation,
  AudioItem,
} from '@/store/api/Predictions/predictionApi';
import { DataTable } from '@/components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useNewDebounce } from '@/hooks/useNewDebounce';
import toast from 'react-hot-toast';

export default function GenerateAudioPage() {
  const [predictionId, setPredictionId] = useState('');
  const [language, setLanguage] = useState('English');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<{ type: 'success' | 'error'; messages: string[] } | null>(
    null
  );

  const [listPageIndex, setListPageIndex] = useState(0);
  const [listPageSize, setListPageSize] = useState(10);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const debouncedFilterSearch = useNewDebounce(filterSearch, 400);
  const debouncedFilterUserId = useNewDebounce(filterUserId, 400);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [generateAudio, { isLoading: isGenerating }] = useGenerateAudioMutation();
  const [triggerGetAudio, { isFetching: isFetchingAudio }] = useLazyGetPredictionAudioQuery();
  const [deleteAudio, { isLoading: isDeleting }] = useDeletePredictionAudioMutation();

  const {
    data: listData,
    isLoading: isListLoading,
    isFetching: isListFetching,
    refetch: refetchList,
  } = useListPredictionAudiosQuery({
    skip: listPageIndex * listPageSize,
    take: listPageSize,
    search: debouncedFilterSearch.trim() || undefined,
    userId: debouncedFilterUserId.trim() || undefined,
    language: filterLanguage === 'all' ? undefined : filterLanguage,
    audioStatus: filterStatus === 'all' ? undefined : filterStatus,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (validationError) {
      timer = setTimeout(() => setValidationError(null), 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [validationError]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (result) {
      timer = setTimeout(() => setResult(null), 7000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [result]);

  useEffect(() => {
    setListPageIndex(0);
  }, [debouncedFilterSearch, debouncedFilterUserId, filterLanguage, filterStatus]);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.error('Audio playback failed:', err);
        });
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
      audioRef.current.muted = vol === 0;
      setIsMuted(vol === 0);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    audioRef.current.muted = newMuteState;
  };

  const handleSkipBackward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
  };

  const handleSkipForward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const downloadAudioFile = async (url: string, id: string, lang: string) => {
    setIsDownloading(true);
    const downloadToast = toast.loading('Preparing audio file download...');
    try {
      // 1. Construct the direct Cloudinary forced-download URL (contains fl_attachment)
      // This is a premium Cloudinary trick: adding /fl_attachment/ forces browser download directly
      // on the current tab without navigating or opening a new tab.
      const downloadUrl = `https://res.cloudinary.com/dabbhqr7g/video/upload/fl_attachment/predictions-audio/prediction_${id}_${lang}.mp3`;

      // 2. Perform a lightweight HEAD request to verify that the file exists on Cloudinary
      const testResponse = await fetch(downloadUrl, { method: 'HEAD' });
      if (!testResponse.ok) {
        throw new Error(`Cloudinary file not found: status ${testResponse.status}`);
      }

      // 3. Trigger direct browser download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `prediction_${id}_${lang}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started!', { id: downloadToast });
    } catch (err) {
      console.warn('Direct Cloudinary download failed, falling back to S3 blob fetch:', err);
      // Fallback: try fetching the original S3 URL as a blob
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `prediction_${id || 'audio'}_${lang}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Download started!', { id: downloadToast });
      } catch (fallbackErr) {
        console.error('Fallback download failed, opening in new tab:', fallbackErr);
        window.open(url, '_blank');
        toast.dismiss(downloadToast);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleGenerateAudio = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setResult(null);
    setAudioUrl(null);

    const cleanId = predictionId.trim();
    if (!cleanId) {
      setValidationError('Prediction ID is required.');
      return;
    }

    try {
      const response = await generateAudio({
        predictionId: cleanId,
        language,
      }).unwrap();

      setResult({
        type: 'success',
        messages: [response.message || 'Audio generation requested successfully.'],
      });

      refetchList();

      await handleFetchAudio(false);
    } catch (err: any) {
      console.error('Generate Audio Error:', err);
      let errors: string[] = [];

      if (err?.data?.message) {
        errors = Array.isArray(err.data.message) ? err.data.message : [String(err.data.message)];
      } else if (err?.data?.error) {
        errors = [String(err.data.error)];
      } else if (err?.error) {
        errors = [String(err.error)];
      } else {
        errors = [
          'Failed to trigger audio generation. Please check the prediction ID and try again.',
        ];
      }

      setResult({
        type: 'error',
        messages: errors,
      });
    }
  };

  const handleFetchAudio = async (showSuccessBanner = true) => {
    setValidationError(null);
    const cleanId = predictionId.trim();
    if (!cleanId) {
      setValidationError('Prediction ID is required.');
      return;
    }

    try {
      const response = await triggerGetAudio({
        predictionId: cleanId,
        language,
      }).unwrap();

      if (response.status === 'Success' && response.url) {
        setAudioUrl(response.url);
        if (showSuccessBanner) {
          setResult({
            type: 'success',
            messages: ['Audio file retrieved successfully.'],
          });
        }
      } else {
        setResult({
          type: 'error',
          messages: ['Audio file not found or not ready yet. Please generate it first.'],
        });
      }
    } catch (err: any) {
      console.error('Fetch Audio Error:', err);
      let errors: string[] = [];

      if (err?.data?.message) {
        errors = Array.isArray(err.data.message) ? err.data.message : [String(err.data.message)];
      } else if (err?.status === 404) {
        errors = [
          'Audio not found. It might not have been generated yet for this prediction and language.',
        ];
      } else if (err?.error) {
        errors = [String(err.error)];
      } else {
        errors = ['Failed to retrieve audio. Please check the prediction ID and language.'];
      }

      setResult({
        type: 'error',
        messages: errors,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    try {
      const response = await deleteAudio(deleteConfirmId).unwrap();
      toast.success(response.message || 'Audio deleted successfully.');
      setDeleteConfirmId(null);
      refetchList();
    } catch (err: any) {
      console.error('Delete Audio Error:', err);
      toast.error(err?.data?.message || 'Failed to delete audio.');
    }
  };

  const columns = useMemo<ColumnDef<AudioItem>[]>(
    () => [
      {
        id: 'sno',
        header: 'S. No.',
        cell: ({ row, table }) => {
          const { pageIndex, pageSize } = (table.options.meta as any)?.pagination ?? {
            pageIndex: 0,
            pageSize: 10,
          };
          return pageIndex * pageSize + row.index + 1;
        },
      },
      {
        accessorKey: 'id',
        header: 'Audio ID',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-500">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'predictionId',
        header: 'Prediction ID',
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-1.5">
            <span className="font-mono text-xs text-slate-700 font-medium">
              {row.original.predictionId}
            </span>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(row.original.predictionId);
                toast.success('Prediction ID copied to clipboard');
              }}
              className="p-1 text-slate-400 hover:text-slate-650 hover:bg-slate-100 rounded transition-all"
              title="Copy ID"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        ),
      },
      {
        accessorKey: 'language',
        header: 'Language',
      },
      {
        accessorKey: 'audioStatus',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.audioStatus;
          let colorClass = 'bg-slate-50 text-slate-700 border-slate-200';
          if (status === 'Success') {
            colorClass = 'bg-emerald-55 text-emerald-700 border-emerald-200 bg-emerald-50';
          } else if (status === 'Pending') {
            colorClass = 'bg-amber-55 text-amber-700 border-amber-200 bg-amber-50';
          } else if (status === 'Processing') {
            colorClass = 'bg-blue-55 text-blue-700 border-blue-200 bg-blue-50';
          } else if (status === 'Failed') {
            colorClass = 'bg-rose-55 text-rose-700 border-rose-200 bg-rose-50';
          } else if (status === 'Draft') {
            colorClass = 'bg-slate-55 text-slate-700 border-slate-200 bg-slate-50';
          }
          return (
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${colorClass}`}>
              {status}
            </span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        cell: ({ row }) => {
          const dateStr = row.original.createdAt;
          if (!dateStr) return '-';
          try {
            return new Date(dateStr).toLocaleString();
          } catch {
            return dateStr;
          }
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const item = row.original;
          const isSuccess = item.audioStatus === 'Success' && item.audioUrl;
          return (
            <div className="flex items-center justify-center gap-1.5">
              <button
                type="button"
                disabled={!isSuccess}
                onClick={() => {
                  setAudioUrl(item.audioUrl);
                  setPredictionId(item.predictionId);
                  setLanguage(item.language);
                  toast.success('Audio loaded to Console');
                }}
                className={`p-1.5 rounded-lg border transition-all ${
                  isSuccess
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100 hover:scale-105'
                    : 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                }`}
                title="Load & Play in Console"
              >
                <Play className="w-3.5 h-3.5 fill-current" />
              </button>

              <button
                type="button"
                disabled={!isSuccess || isDownloading}
                onClick={() => downloadAudioFile(item.audioUrl!, item.predictionId, item.language)}
                className={`p-1.5 rounded-lg border transition-all ${
                  isSuccess
                    ? 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:scale-105'
                    : 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                }`}
                title="Download Audio"
              >
                <Download className="w-3.5 h-3.5" />
              </button>

              {/* Delete */}
              <button
                type="button"
                onClick={() => setDeleteConfirmId(item.predictionId)}
                className="p-1.5 rounded-lg border bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100 hover:scale-105 transition-all"
                title="Delete Audio"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        },
      },
    ],
    [isDownloading]
  );

  return (
    <div className="min-h-screen bg-slate-50/50 space-y-8 font-lato">
      <div className="flex items-center justify-between border-b pb-4 border-slate-200">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-spectral">
            Prediction Audio Manager
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Generate prediction audios, listen, download directly, and manage all database files.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
        <div className="xl:col-span-5 bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="bg-[linear-gradient(to_right,#082261,#34065c)] p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl pointer-events-none" />
            <div className="flex items-center gap-3 relative z-10">
              <div className="bg-white/20 rounded-full p-2">
                <Volume2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold font-spectral tracking-wide">Generate Audio</h2>
                <p className="text-white/70 text-xs mt-0.5">Generate or fetch prediction audio</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleGenerateAudio} className="p-6 space-y-6">
            <div className="space-y-1.5">
              <Label htmlFor="predictionId" className="text-sm font-semibold text-gray-700">
                Prediction ID <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="predictionId"
                  placeholder="e.g. 6a019be8cf8179ae9b724284"
                  value={predictionId}
                  onChange={(e) => {
                    setPredictionId(e.target.value);
                    setValidationError(null);
                    setResult(null);
                  }}
                  className="font-mono text-sm pl-10 h-11 border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 rounded-lg transition-all"
                  required
                />
                <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="language" className="text-sm font-semibold text-gray-700">
                Language <span className="text-red-500">*</span>
              </Label>
              <Select
                value={language}
                onValueChange={(val) => {
                  setLanguage(val);
                  setValidationError(null);
                  setResult(null);
                }}
              >
                <SelectTrigger className="w-full h-11 border-gray-200 focus:border-indigo-500 rounded-lg transition-all">
                  <SelectValue placeholder="Select Language" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Hindi">Hindi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {validationError && (
              <div className="flex items-start gap-3 rounded-xl p-4 bg-amber-50 border border-amber-200">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-700 font-medium">{validationError}</p>
              </div>
            )}

            {result && (
              <div
                className={`flex items-start gap-3 rounded-xl p-4 border ${
                  result.type === 'success'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                {result.type === 'success' ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                )}
                <div className="text-sm space-y-1">
                  {result.messages.map((msg, idx) => (
                    <p
                      key={idx}
                      className={`font-medium ${result.type === 'success' ? 'text-green-700' : 'text-red-700'}`}
                    >
                      {msg}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="submit"
                disabled={!predictionId.trim() || isGenerating || isFetchingAudio}
                className="
                  w-full py-3 px-4 rounded-xl font-bold font-spectral text-sm uppercase tracking-wider
                  transition-all duration-300 ease-in-out
                  bg-[linear-gradient(to_right,#082261,#34065c)] text-white
                  hover:opacity-95 hover:shadow-md active:scale-[0.98]
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100
                  flex items-center justify-center gap-2
                "
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="animate-spin h-4 w-4 text-white" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Volume2 className="h-4 w-4" />
                    Generate Audio
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleFetchAudio(true)}
                disabled={!predictionId.trim() || isGenerating || isFetchingAudio}
                className="
                  w-full py-3 px-4 rounded-xl font-bold font-spectral text-sm uppercase tracking-wider
                  border border-gray-200 text-gray-700 hover:bg-gray-50
                  transition-all duration-300 ease-in-out active:scale-[0.98]
                  disabled:opacity-40 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2
                "
              >
                {isFetchingAudio ? (
                  <>
                    <RefreshCw className="animate-spin h-4 w-4 text-gray-600" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <Music className="h-4 w-4 text-gray-650" />
                    Fetch Audio
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="xl:col-span-7 bg-white rounded-2xl shadow-lg border border-slate-100 p-6 h-full min-h-[380px] flex flex-col justify-between text-slate-900">
          <div className="border-b pb-3 border-slate-100 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase text-slate-450 tracking-widest flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-indigo-500 animate-ping" />
              Admin Audio Playback Console
            </h3>
            {audioUrl && (
              <button
                onClick={() => setAudioUrl(null)}
                className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-lg transition-all"
                title="Clear Console"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {audioUrl ? (
            <div className="py-6 space-y-6">
              <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleAudioEnded}
                preload="metadata"
                className="hidden"
              />

              <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-2xl relative overflow-hidden border border-slate-800">
                <div className="absolute right-6 top-6 w-32 h-12 opacity-20 flex items-end justify-center gap-1 pointer-events-none">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        height: isPlaying ? `${Math.floor(Math.random() * 40) + 10}px` : '4px',
                        transition: 'height 0.2s ease-in-out',
                      }}
                      className="w-1.5 bg-indigo-400 rounded-full"
                    />
                  ))}
                </div>

                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-3 shadow-lg shadow-indigo-500/35">
                    <Music className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm leading-tight text-white tracking-wide">
                      Prediction Audio ({language})
                    </h4>
                    <p className="text-xs text-slate-450 font-mono mt-0.5 max-w-[280px] truncate">
                      ID: {predictionId}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => downloadAudioFile(audioUrl, predictionId, language)}
                    disabled={isDownloading}
                    className="ml-auto bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 hover:text-white p-2.5 rounded-xl transition-all duration-300 border border-slate-750 flex items-center justify-center"
                    title="Download Audio"
                  >
                    {isDownloading ? (
                      <RefreshCw className="h-4 w-4 animate-spin text-slate-300" />
                    ) : (
                      <Download className="h-4.5 w-4.5" />
                    )}
                  </button>
                </div>

                <div className="space-y-2 mb-6">
                  <div className="flex justify-between text-[11px] text-slate-400 font-semibold font-mono">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max={duration || 100}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-slate-700 accent-indigo-500 focus:outline-none transition-all duration-300"
                    style={{
                      background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(currentTime / (duration || 1)) * 100}%, #334155 ${(currentTime / (duration || 1)) * 100}%, #334155 100%)`,
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleSkipBackward}
                    className="p-2 text-slate-400 hover:text-white transition-colors"
                    title="Rewind 10s"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={handlePlayPause}
                    className="
                      h-12 w-12 rounded-full flex items-center justify-center
                      bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-xl shadow-indigo-500/20
                      hover:scale-105 hover:shadow-indigo-500/30 transition-all duration-300 active:scale-95
                    "
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5 fill-current" />
                    ) : (
                      <Play className="h-5 w-5 fill-current ml-0.5" />
                    )}
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={toggleMute}
                      className="p-2 text-slate-400 hover:text-white transition-colors"
                    >
                      {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="w-16 h-1 rounded-lg appearance-none cursor-pointer bg-slate-750 accent-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-400">
              <div className="bg-slate-50 border border-slate-100 rounded-full p-4 mb-4">
                <Music className="h-8 w-8 text-slate-300" />
              </div>
              <h4 className="font-semibold text-slate-750 text-sm">No Audio Loaded</h4>
              <p className="text-xs text-slate-450 mt-1 max-w-[280px] leading-relaxed">
                Generate an audio, fetch one, or click play on any file in the database list below
                to load it into the console.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 space-y-6">
        <div className="flex items-center justify-between border-b pb-4 border-slate-100">
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-spectral">
              Available Prediction Audios
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Explore and manage generated prediction audios in the system.
            </p>
          </div>
          <button
            onClick={() => refetchList()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-all active:scale-[0.97]"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${(isListLoading || isListFetching) && 'animate-spin'}`}
            />
            Refresh List
          </button>
        </div>

        <div className="bg-slate-50/50 p-5 rounded-xl border border-slate-150 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Filter className="h-4.5 w-4.5 text-slate-400" />
            Search & Parameters Filter
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="filterSearch" className="text-xs font-semibold text-slate-600">
                Search
              </Label>
              <div className="relative">
                <Input
                  id="filterSearch"
                  placeholder="Prediction / Audio ID..."
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  className="pl-9 h-10 border-slate-200 rounded-lg text-sm bg-white"
                />
                <Search className="absolute left-3 top-3.5 h-3.5 w-3.5 text-slate-400" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="filterUserId" className="text-xs font-semibold text-slate-600">
                User ID
              </Label>
              <div className="relative">
                <Input
                  id="filterUserId"
                  placeholder="Filter by User ID..."
                  value={filterUserId}
                  onChange={(e) => setFilterUserId(e.target.value)}
                  className="pl-9 h-10 border-slate-200 rounded-lg text-sm bg-white"
                />
                <Search className="absolute left-3 top-3.5 h-3.5 w-3.5 text-slate-400" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="filterLanguage" className="text-xs font-semibold text-slate-600">
                Language
              </Label>
              <Select value={filterLanguage} onValueChange={setFilterLanguage}>
                <SelectTrigger
                  id="filterLanguage"
                  className="h-10 border-slate-200 rounded-lg text-sm bg-white"
                >
                  <SelectValue placeholder="All Languages" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all">All Languages</SelectItem>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Hindi">Hindi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Audio Status */}
            <div className="space-y-1.5">
              <Label htmlFor="filterStatus" className="text-xs font-semibold text-slate-600">
                Audio Status
              </Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger
                  id="filterStatus"
                  className="h-10 border-slate-200 rounded-lg text-sm bg-white"
                >
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Success">Success</SelectItem>
                  <SelectItem value="Processing">Processing</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200/60">
            <button
              type="button"
              onClick={() => {
                setFilterSearch('');
                setFilterUserId('');
                setFilterLanguage('all');
                setFilterStatus('all');
                toast.success('Filters cleared');
              }}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-all active:scale-[0.97]"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="w-full">
          <DataTable
            data={listData?.data || []}
            columns={columns}
            enableSearch={false}
            enableFilters={false}
            enableColumnVisibility={false}
            manualPagination={true}
            pageSize={listPageSize}
            pageCount={Math.ceil((listData?.total || 0) / listPageSize)}
            onPaginationChange={({ pageIndex, pageSize }) => {
              setListPageIndex(pageIndex);
              setListPageSize(pageSize);
            }}
            isLoading={isListLoading || isListFetching}
          />
        </div>
      </div>

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-[linear-gradient(to_right,#082261,#34065c)] p-5 text-white flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold font-spectral">Confirm Delete</p>
                <p className="text-white/70 text-xs mt-0.5">This action cannot be undone</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to permanently delete this audio record? This will remove the
                file from the database and storage.
              </p>
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-150 font-mono text-xs text-slate-500 break-all">
                ID: {deleteConfirmId}
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
